import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  X,
  Clock,
  TrendingUp,
  Star,
  Package,
  ArrowRight,
  Filter,
  FileText,
  FolderTree,
  Globe,
  Loader2,
} from "lucide-react";
import { productService } from "../../api/services/productService";
import { blogService } from "../../api/services/blogService";

// Helper function to get full image URL
const getFullImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  if (path.startsWith("/uploads")) {
    const apiBaseUrl =
      import.meta.env.VITE_API_UPLAODS || "http://localhost:3000";
    return `${apiBaseUrl}${path}`;
  }
  return path;
};

// Product Image Component for search results
const ProductImage = ({ product, className }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    const loadImage = () => {
      // Priority 1: mainImagePath (new path-based storage)
      if (product?.mainImagePath) {
        setImageUrl(getFullImageUrl(product.mainImagePath));
        setImageLoading(false);
        return;
      }

      // Priority 2: gallery images path
      if (product?.galleryImages && product.galleryImages.length > 0) {
        const firstImage = product.galleryImages[0];
        if (firstImage?.path) {
          setImageUrl(getFullImageUrl(firstImage.path));
          setImageLoading(false);
          return;
        }
        if (firstImage?.data) {
          setImageUrl(`data:${firstImage.mimeType};base64,${firstImage.data}`);
          setImageLoading(false);
          return;
        }
      }

      // Priority 3: imageBase64 (legacy BLOB)
      if (product?.imageBase64) {
        setImageUrl(product.imageBase64);
        setImageLoading(false);
        return;
      }

      // Priority 4: image URL
      if (product?.image) {
        setImageUrl(product.image);
        setImageLoading(false);
        return;
      }

      setImageLoading(false);
    };

    loadImage();
  }, [product]);

  if (imageLoading && !imageError) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <Loader2 size={20} className="animate-spin text-primary-500" />
      </div>
    );
  }

  if (imageUrl && !imageError) {
    return (
      <img
        src={imageUrl}
        alt={product?.imageAlt || product?.name || "Product"}
        className={`object-cover ${className}`}
        onError={() => {
          console.error(`Failed to load product image: ${imageUrl}`);
          setImageError(true);
          setImageLoading(false);
        }}
      />
    );
  }

  return (
    <div
      className={`flex items-center justify-center ${className} bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 rounded-lg`}
    >
      <Package size={24} className="text-gray-400" />
    </div>
  );
};

const SearchBar = ({
  placeholder = "Search products, categories, blogs...",
  showSuggestions = true,
  onSearch,
  className = "",
  size = "default",
  variant = "default",
  showFilterButton = false,
  onFilterClick,
}) => {
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState({
    products: [],
    categories: [],
    blogs: [],
    pages: [],
  });
  const [recentSearches, setRecentSearches] = useState([]);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Popular searches
  const popularSearches = [
    "Potato Chips",
    "Food & Beverages",
    "Export Guide",
    "Cleaning Products",
    "Wholesale Trade",
  ];

  // Predefined pages
  const pages = [
    { name: "Home", path: "/", icon: Globe },
    { name: "About Us", path: "/about", icon: FileText },
    { name: "Products", path: "/products", icon: Package },
    { name: "Categories", path: "/categories", icon: FolderTree },
    { name: "Blog", path: "/blog", icon: FileText },
    { name: "Contact", path: "/contact", icon: Globe },
    { name: "Support", path: "/support", icon: Globe },
  ];

  useEffect(() => {
    // Load recent searches from localStorage
    const savedSearches = localStorage.getItem("recentSearches");
    if (savedSearches) {
      setRecentSearches(JSON.parse(savedSearches).slice(0, 5));
    }

    // Close suggestions when clicking outside
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsFocused(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (query.length >= 2) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        performSearch(query);
      }, 300);
    } else if (query.length === 0) {
      setSearchResults({ products: [], categories: [], blogs: [], pages: [] });
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [query]);

  const performSearch = async (searchTerm) => {
    if (!searchTerm.trim()) return;

    setIsLoading(true);
    try {
      // Search products
      let productsData = [];
      try {
        const productsResponse = await productService.quickSearch(
          searchTerm,
          5,
        );
        console.log("Products API Response:", productsResponse);

        // Parse product response - handle multiple response structures
        if (productsResponse?.data?.data) {
          productsData = productsResponse.data.data;
        } else if (productsResponse?.data?.products) {
          productsData = productsResponse.data.products;
        } else if (
          productsResponse?.data &&
          Array.isArray(productsResponse.data)
        ) {
          productsData = productsResponse.data;
        } else if (
          productsResponse?.products &&
          Array.isArray(productsResponse.products)
        ) {
          productsData = productsResponse.products;
        } else if (Array.isArray(productsResponse)) {
          productsData = productsResponse;
        } else if (productsResponse?.data?.items) {
          productsData = productsResponse.data.items;
        }

        // Ensure productsData is an array
        productsData = Array.isArray(productsData) ? productsData : [];
        console.log("Parsed Products:", productsData);
      } catch (err) {
        console.error("Product search error:", err);
        productsData = [];
      }

      // Search blogs
      let blogsData = [];
      try {
        const blogsResponse = await blogService.searchBlogs(searchTerm, {
          limit: 5,
        });
        console.log("Blogs API Response:", blogsResponse);

        // Parse blog response - handle multiple response structures
        if (blogsResponse?.data?.data) {
          blogsData = blogsResponse.data.data;
        } else if (blogsResponse?.data?.blogs) {
          blogsData = blogsResponse.data.blogs;
        } else if (blogsResponse?.data && Array.isArray(blogsResponse.data)) {
          blogsData = blogsResponse.data;
        } else if (blogsResponse?.blogs && Array.isArray(blogsResponse.blogs)) {
          blogsData = blogsResponse.blogs;
        } else if (Array.isArray(blogsResponse)) {
          blogsData = blogsResponse;
        } else if (blogsResponse?.data?.items) {
          blogsData = blogsResponse.data.items;
        }

        // Ensure blogsData is an array
        blogsData = Array.isArray(blogsData) ? blogsData : [];
        console.log("Parsed Blogs:", blogsData);
      } catch (err) {
        console.error("Blog search error:", err);
        blogsData = [];
      }

      // Filter categories based on search term
      const categoriesData = [
        { name: "Food & Beverages", slug: "food", icon: Package, count: 0 },
        {
          name: "Non-Food Products",
          slug: "non-food",
          icon: Package,
          count: 0,
        },
      ].filter((cat) =>
        cat.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );

      // Filter pages based on search term
      const filteredPages = pages.filter((page) =>
        page.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );

      setSearchResults({
        products: productsData,
        categories: categoriesData,
        blogs: blogsData,
        pages: filteredPages,
      });
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveRecentSearch = (searchTerm) => {
    const updatedSearches = [
      searchTerm,
      ...recentSearches.filter((s) => s !== searchTerm),
    ].slice(0, 5);

    setRecentSearches(updatedSearches);
    localStorage.setItem("recentSearches", JSON.stringify(updatedSearches));
  };

  const handleSearch = (searchTerm = query) => {
    const term = searchTerm.trim();
    if (!term) return;

    saveRecentSearch(term);

    if (onSearch) {
      onSearch(term);
    } else {
      navigate(`/products?search=${encodeURIComponent(term)}`);
    }

    setIsFocused(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSearch();
  };

  const handleSuggestionClick = (suggestion, type, path = null) => {
    setQuery(suggestion);

    if (path) {
      navigate(path);
      setIsFocused(false);
      return;
    }

    if (type === "product") {
      navigate(`/products/${suggestion}`);
    } else if (type === "category") {
      navigate(`/categories/${suggestion}`);
    } else if (type === "blog") {
      navigate(`/blog/${suggestion}`);
    } else {
      handleSearch(suggestion);
    }

    setIsFocused(false);
  };

  const clearSearch = () => {
    setQuery("");
    setIsFocused(false);
    setSearchResults({ products: [], categories: [], blogs: [], pages: [] });
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem("recentSearches");
  };

  const getVariantStyles = () => {
    switch (variant) {
      case "minimal":
        return "bg-transparent border-0 shadow-none";
      case "filled":
        return "bg-gray-100 dark:bg-gray-800 border-transparent";
      default:
        return "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 shadow-sm";
    }
  };

  const getSizeStyles = () => {
    switch (size) {
      case "small":
        return "h-10 text-sm";
      case "large":
        return "h-14 text-lg";
      default:
        return "h-12 text-base";
    }
  };

  const SearchSuggestions = () => (
    <AnimatePresence>
      {isFocused && showSuggestions && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden z-50 max-h-[80vh] overflow-y-auto"
        >
          <div className="p-4">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
                <span className="ml-2 text-gray-500">Searching...</span>
              </div>
            )}

            {/* Search Results */}
            {!isLoading && query.length >= 2 && (
              <>
                {/* Products Section */}
                {searchResults.products.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Package className="w-4 h-4 text-primary-500" />
                      <h4 className="font-semibold dark:text-white">
                        Products ({searchResults.products.length})
                      </h4>
                    </div>
                    <div className="space-y-2">
                      {searchResults.products.slice(0, 5).map((product) => (
                        <button
                          key={product.id || product.uuid}
                          onClick={() =>
                            handleSuggestionClick(product.slug, "product")
                          }
                          className="w-full flex items-center justify-between p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors group"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            {/* Updated Product Image with proper handling */}
                            <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900">
                              <ProductImage
                                product={product}
                                className="w-full h-full"
                              />
                            </div>
                            <div className="flex-1 text-left">
                              <p className="font-medium text-gray-900 dark:text-white group-hover:text-primary-600 line-clamp-1">
                                {product.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                SKU: {product.sku} •{" "}
                                {product.category === "food"
                                  ? "Food & Beverages"
                                  : "Non-Food"}
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Categories Section */}
                {searchResults.categories.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <FolderTree className="w-4 h-4 text-primary-500" />
                      <h4 className="font-semibold dark:text-white">
                        Categories
                      </h4>
                    </div>
                    <div className="space-y-2">
                      {searchResults.categories.map((category) => (
                        <button
                          key={category.slug}
                          onClick={() =>
                            handleSuggestionClick(category.slug, "category")
                          }
                          className="w-full flex items-center justify-between p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors group"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                              <category.icon className="w-5 h-5 text-white" />
                            </div>
                            <div className="text-left">
                              <p className="font-medium text-gray-900 dark:text-white group-hover:text-primary-600">
                                {category.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                Browse {category.name.toLowerCase()}
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Blog Posts Section */}
                {searchResults.blogs.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-4 h-4 text-primary-500" />
                      <h4 className="font-semibold dark:text-white">
                        Blog Posts ({searchResults.blogs.length})
                      </h4>
                    </div>
                    <div className="space-y-2">
                      {searchResults.blogs.slice(0, 5).map((blog) => (
                        <button
                          key={blog.id || blog.uuid}
                          onClick={() =>
                            handleSuggestionClick(blog.slug, "blog")
                          }
                          className="w-full flex items-center justify-between p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors group"
                        >
                          <div className="flex items-center gap-3 flex-1">
                            {blog.featuredImage ? (
                              <img
                                src={blog.featuredImage}
                                alt={blog.title}
                                className="w-10 h-10 rounded-lg object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                                <FileText className="w-5 h-5 text-white" />
                              </div>
                            )}
                            <div className="flex-1 text-left">
                              <p className="font-medium text-gray-900 dark:text-white line-clamp-1 group-hover:text-primary-600">
                                {blog.title}
                              </p>
                              <p className="text-xs text-gray-500">
                                {blog.category || "Uncategorized"} •{" "}
                                {blog.createdAt
                                  ? new Date(
                                      blog.createdAt,
                                    ).toLocaleDateString()
                                  : "Recent"}
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pages Section */}
                {searchResults.pages.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Globe className="w-4 h-4 text-primary-500" />
                      <h4 className="font-semibold dark:text-white">Pages</h4>
                    </div>
                    <div className="space-y-2">
                      {searchResults.pages.map((page) => {
                        const Icon = page.icon;
                        return (
                          <button
                            key={page.path}
                            onClick={() =>
                              handleSuggestionClick(
                                page.name,
                                "page",
                                page.path,
                              )
                            }
                            className="w-full flex items-center justify-between p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors group"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <div className="text-left">
                                <p className="font-medium text-gray-900 dark:text-white group-hover:text-primary-600">
                                  {page.name}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Navigate to {page.name}
                                </p>
                              </div>
                            </div>
                            <ArrowRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* No Results */}
                {searchResults.products.length === 0 &&
                  searchResults.categories.length === 0 &&
                  searchResults.blogs.length === 0 &&
                  searchResults.pages.length === 0 && (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
                        <Search className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-500 dark:text-gray-400">
                        No results found for "{query}"
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        Try searching with different keywords
                      </p>
                    </div>
                  )}
              </>
            )}

            {/* Recent Searches - Show only when query is empty */}
            {!isLoading && query.length === 0 && recentSearches.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-gray-500" />
                    <h4 className="font-semibold dark:text-white">
                      Recent Searches
                    </h4>
                  </div>
                  <button
                    onClick={clearRecentSearches}
                    className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Clear all
                  </button>
                </div>
                <div className="space-y-2">
                  {recentSearches.map((search, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(search)}
                      className="w-full flex items-center justify-between p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <Clock className="w-4 h-4 text-gray-400 group-hover:text-primary-500" />
                        <span className="text-gray-700 dark:text-gray-300 group-hover:text-primary-600">
                          {search}
                        </span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Popular Searches - Show only when query is empty */}
            {!isLoading && query.length === 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <TrendingUp className="w-4 h-4 text-gray-500" />
                  <h4 className="font-semibold dark:text-white">
                    Popular Searches
                  </h4>
                </div>
                <div className="space-y-2">
                  {popularSearches.map((search, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(search)}
                      className="w-full flex items-center justify-between p-3 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`p-2 rounded-lg ${index < 2 ? "bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400" : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"}`}
                        >
                          {index < 2 ? (
                            <Star className="w-3 h-3" />
                          ) : (
                            <Package className="w-3 h-3" />
                          )}
                        </div>
                        <span className="text-gray-700 dark:text-gray-300 group-hover:text-primary-600">
                          {search}
                        </span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Search Button */}
          {query.length >= 2 && (
            <div className="border-t border-gray-200 dark:border-gray-800 p-4 bg-gray-50 dark:bg-gray-900/50">
              <button
                onClick={() => handleSearch()}
                className="w-full py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-xl flex items-center justify-center gap-2 hover:from-primary-600 hover:to-secondary-600 transition-all"
              >
                <Search className="w-4 h-4" />
                Search for "{query}"
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        <div
          className={`relative flex items-center ${getSizeStyles()} ${getVariantStyles()} border rounded-2xl transition-all duration-300 ${isFocused ? "ring-2 ring-primary-500 border-primary-500" : ""}`}
        >
          {/* Search Icon */}
          <div className="absolute left-4 text-gray-400">
            <Search className={`${size === "small" ? "w-4 h-4" : "w-5 h-5"}`} />
          </div>

          {/* Search Input */}
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            placeholder={placeholder}
            className={`w-full pl-12 pr-12 bg-transparent border-none focus:outline-none focus:ring-0 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 ${size === "small" ? "text-sm" : ""}`}
          />

          {/* Loading Indicator */}
          {isLoading && (
            <div className="absolute right-4">
              <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
            </div>
          )}

          {/* Clear Button */}
          {query && !isLoading && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className={`${size === "small" ? "w-4 h-4" : "w-5 h-5"}`} />
            </button>
          )}

          {/* Filter Button (Optional) */}
          {showFilterButton && onFilterClick && (
            <button
              type="button"
              onClick={onFilterClick}
              className="absolute right-12 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
              title="Open filters"
            >
              <Filter
                className={`${size === "small" ? "w-4 h-4" : "w-5 h-5"}`}
              />
            </button>
          )}
        </div>

        {/* Search Button for Large Variant */}
        {size === "large" && (
          <button
            type="submit"
            className="absolute right-2 top-2 bottom-2 px-6 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-xl hover:from-primary-600 hover:to-secondary-600 transition-all flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            Search
          </button>
        )}
      </form>

      {/* Search Suggestions */}
      <SearchSuggestions />

      {/* Mobile Search Overlay */}
      {isFocused && showSuggestions && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setIsFocused(false)}
        />
      )}
    </div>
  );
};

export default SearchBar;
